/**
 * A word-level difference between the master's wording and the printed label's.
 *
 * A regulatory divergence is usually a handful of words inside a wall of
 * boilerplate — "16.0" against "17.0", a bracket dropped — so the job is to
 * point at the words, not to restate the sentence.
 */
export type DiffOp = "same" | "added" | "removed"

export interface DiffPart {
  op: DiffOp
  text: string
}

/** Whitespace is kept as its own tokens so each side rebuilds its original text exactly. */
function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((t) => t !== "")
}

/**
 * Past this the quadratic LCS below locks the main thread — the largest master
 * text in QA is ~90k characters — and the caller shows plain text instead.
 */
const MAX_TOKENS = 1200

export function diffWords(before: string, after: string): DiffPart[] | null {
  const a = tokenize(before)
  const b = tokenize(after)
  if (a.length + b.length > MAX_TOKENS) return null

  // LCS length of a[i:] and b[j:], flat-indexed; the +1 row and column are the
  // zero border, so every lookup below is in bounds without a guard.
  const width = b.length + 1
  const lengths = new Uint16Array((a.length + 1) * width)
  const at = (i: number, j: number): number => lengths[i * width + j] ?? 0
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lengths[i * width + j] =
        a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1))
    }
  }

  const raw: DiffPart[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    const x = a[i]
    const y = b[j]
    if (x === undefined || y === undefined) break
    if (x === y) {
      raw.push({ op: "same", text: x })
      i++
      j++
    } else if (at(i + 1, j) >= at(i, j + 1)) {
      raw.push({ op: "removed", text: x })
      i++
    } else {
      raw.push({ op: "added", text: y })
      j++
    }
  }
  for (const text of a.slice(i)) raw.push({ op: "removed", text })
  for (const text of b.slice(j)) raw.push({ op: "added", text })
  return coalesce(raw)
}

/**
 * Merge runs so a changed phrase is one mark rather than a mark per word.
 *
 * Two changed words are separated by a space the texts share. Left alone it
 * splits one phrase into two marks with an unmarked gap; absorbed into the
 * change, "x y" marks as the phrase it is.
 */
function coalesce(parts: readonly DiffPart[]): DiffPart[] {
  const out: DiffPart[] = []
  for (const part of parts) {
    const last = out.at(-1)
    const prior = out.at(-2)
    if (last?.op === part.op) {
      last.text += part.text
    } else if (
      part.op !== "same" &&
      last?.op === "same" &&
      last.text.trim() === "" &&
      prior?.op === part.op
    ) {
      prior.text += last.text + part.text
      out.pop()
    } else {
      out.push({ ...part })
    }
  }
  return out
}

/** The parts one side shows: what both share, plus the words only it has. */
export function sideParts(parts: readonly DiffPart[], side: "master" | "printed"): DiffPart[] {
  const own: DiffOp = side === "master" ? "removed" : "added"
  return parts.filter((p) => p.op === "same" || p.op === own)
}
